import React, { useEffect, useState } from "react";

export default function QuestionRenderer({ question, userAnswer, onAnswer }) {
  const { type, prompt, choices } = question;
  const [selected, setSelected] = useState(userAnswer || []);
  const [activeInsert, setActiveInsert] = useState(userAnswer || null);
  const [dragAnswer, setDragAnswer] = useState(userAnswer || { left: [], right: [] });

  useEffect(() => {
    if (userAnswer) {
      setSelected(userAnswer);
      if (userAnswer.left || userAnswer.right) {
        setDragAnswer(userAnswer);
      }
    }
  }, [question.id, userAnswer]);

  switch (type) {
    case "single":
      return (
        <div className="space-y-4">
          <p className="mb-2 font-semibold">{prompt}</p>
          <div className="grid gap-2">
            {choices.map((choice, index) => {
              const isSelected = userAnswer === choice.text;
              return (
                <button
                  key={index}
                  onClick={() => onAnswer(choice.text)}
                  className={`w-full text-left px-4 py-3 rounded-md border 
                    ${isSelected ? "bg-blue-600 text-white border-blue-700" : "bg-blue-100 text-blue-800 border-blue-200"}
                    hover:bg-blue-200 transition-all`}
                >
                  {choice.text}
                </button>
              );
            })}
          </div>
        </div>
      );

    case "multi":
      return (
        <div className="space-y-4">
          <p className="mb-2 font-semibold">{prompt}</p>
          <div className="grid gap-2">
            {choices.map((choice, index) => {
              const isSelected = userAnswer?.includes(choice.text);
              return (
                <button
                  key={index}
                  onClick={() => {
                    let newAnswer = userAnswer ? [...userAnswer] : [];
                    if (isSelected) {
                      newAnswer = newAnswer.filter((text) => text !== choice.text);
                    } else {
                      newAnswer.push(choice.text);
                    }
                    onAnswer(newAnswer);
                  }}
                  className={`w-full text-left px-4 py-3 rounded-md border 
                    ${isSelected ? "bg-blue-600 text-white border-blue-700" : "bg-blue-100 text-blue-800 border-blue-200"}
                    hover:bg-blue-200 transition-all`}
                >
                  {choice.text}
                </button>
              );
            })}
          </div>
        </div>
      );

    case "insert":
      const highlightedText = question.paragraphText.replace(/█ \[([A-D])\]/g, (match, label) => {
        if (activeInsert === label) {
          return `<span style="background-color: #fefcbf; border-radius: 4px; padding: 2px 4px;">${question.insertText}</span>`;
        } else {
          return `<span style="font-weight: bold;">█ [${label}]</span>`;
        }
      });

      const handleInsertClick = (label) => {
        setActiveInsert(label);
        onAnswer(label);
      };

      return (
        <div className="space-y-4">
          <p className="mb-2 font-semibold">{prompt}</p>

          <blockquote className="bg-yellow-100 p-3 italic border-l-4 border-yellow-400 rounded">
            {question.insertText}
          </blockquote>

          <div
            className="bg-gray-50 p-4 border rounded whitespace-pre-wrap"
            dangerouslySetInnerHTML={{ __html: highlightedText }}
          />

          <div className="mt-4 space-y-2">
            <p className="font-medium mb-2">Click a position to try the sentence there:</p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {question.options.map((option, index) => {
                const isSelected = activeInsert === option.label;
                return (
                  <button
                    key={index}
                    onClick={() => handleInsertClick(option.label)}
                    className={`w-full px-4 py-2 rounded-md border 
                      ${isSelected ? "bg-blue-600 text-white border-blue-700" : "bg-blue-100 text-blue-800 border-blue-200"}
                      hover:bg-blue-200 transition-all`}
                  >
                    [{option.label}]
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      );

    case "dragdrop1col":
      const handleDrop = (item) => {
        if (selected.length < 3 && !selected.includes(item)) {
          const newSelection = [...selected, item];
          setSelected(newSelection);
          onAnswer(newSelection);
        }
      };

      const handleRemove = (item) => {
        const newSelection = selected.filter((i) => i !== item);
        setSelected(newSelection);
        onAnswer(newSelection);
      };

      return (
        <div className="space-y-6">
          <p className="font-medium">{prompt}</p>

          <div className="border border-gray-300 p-4 bg-gray-50 rounded">
            <p className="font-semibold">Introductory sentence:</p>
            <p className="italic">{question.introSentence}</p>
          </div>

          <div>
            <p className="mb-2 font-semibold">Your summary (drop up to 3 choices):</p>
            <div className="grid gap-2">
              {[0, 1, 2].map((_, i) => (
                <div
                  key={i}
                  className="border-2 border-dashed border-gray-400 p-3 min-h-[60px] bg-white rounded shadow-sm hover:border-blue-400 transition-all"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    const item = e.dataTransfer.getData("text/plain");
                    handleDrop(item);
                  }}
                >
                  {selected[i] && (
                    <div className="flex justify-between items-center text-sm text-gray-800">
                      <span>{selected[i]}</span>
                      <button
                        onClick={() => handleRemove(selected[i])}
                        className="text-red-500 ml-2 text-xs hover:underline"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div>
            <p className="mt-6 mb-2 font-semibold">Answer choices:</p>
            <div className="grid gap-2">
              {question.options
                .filter((option) => !selected.includes(option.text))
                .map((option, index) => (
                  <div
                    key={index}
                    className="p-2 bg-blue-50 border border-blue-300 rounded cursor-move hover:bg-blue-100 transition-all"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", option.text);
                    }}
                  >
                    {option.text}
                  </div>
                ))}
            </div>
          </div>
        </div>
      );

    case "dragdrop2col":
      const handleDrop2Col = (col, item) => {
        if (!dragAnswer.left.includes(item) && !dragAnswer.right.includes(item)) {
          const updated = {
            ...dragAnswer,
            [col]: [...dragAnswer[col], item]
          };
          setDragAnswer(updated);
          onAnswer(updated);
        }
      };

      const handleRemove2Col = (item) => {
        const updated = {
          left: dragAnswer.left.filter((i) => i !== item),
          right: dragAnswer.right.filter((i) => i !== item)
        };
        setDragAnswer(updated);
        onAnswer(updated);
      };

      return (
        <div className="space-y-6">
          <p className="font-medium">{prompt}</p>

          <div className="grid md:grid-cols-2 gap-6">
            {['left', 'right'].map((colKey) => (
              <div
                key={colKey}
                className="border-2 border-dashed border-gray-400 bg-gray-50 p-4 rounded min-h-[160px] hover:border-blue-400 transition-all"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  const item = e.dataTransfer.getData("text/plain");
                  handleDrop2Col(colKey, item);
                }}
              >
                <p className="font-semibold mb-2 text-blue-800">
                  {colKey === 'left' ? question.leftTitle : question.rightTitle}
                </p>

                <div className="space-y-2">
                  {dragAnswer[colKey].map((text, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center bg-white p-2 rounded border border-blue-300 shadow-sm text-sm"
                    >
                      <span>{text}</span>
                      <button
                        onClick={() => handleRemove2Col(text)}
                        className="text-red-500 text-xs hover:underline"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div>
            <p className="mt-6 mb-2 font-semibold">Answer choices:</p>
            <div className="grid gap-2">
              {question.options
                .filter(
                  (option) =>
                    !dragAnswer.left.includes(option.text) &&
                    !dragAnswer.right.includes(option.text)
                )
                .map((option, index) => (
                  <div
                    key={index}
                    className="p-2 bg-blue-100 border border-blue-400 rounded cursor-move hover:bg-blue-200 transition-all"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", option.text);
                    }}
                  >
                    {option.text}
                  </div>
                ))}
            </div>
          </div>
        </div>
      );

    default:
      return <p>Unknown question type: {type}</p>;
  }
}
